import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ServiceContext } from '@msx/platform-services';
import { loadCopilotAccessMap } from '../../utils/copilotAccessMap';
import { TileAgentHandoffContent } from './TileAgentHandoff';
import { trackTileAgentHandoff } from './utils/tileAgentHandoffTelemetry';
import { Panel, PanelType, IconButton } from '@fluentui/react';
import { ArrowClockwiseRegular, ChevronLeftRegular, ChevronDoubleLeftRegular, ChevronDoubleRightRegular } from '@fluentui/react-icons';
import { useMCAPSCopilotTelemetry } from './utils/mcapsCopilotTelemetry';
import SimpleCopilotPanel from './SimpleCopilotPanel';
import { useDispatch } from 'react-redux';
import '../main/Services/Panel.css';

import type { MCAPSCopilotPanelWrapperProps, CopilotPanelSize } from './interfaces';
import {
  COPILOT_PANEL_WIDTHS,
  COPILOT_PANEL_STORAGE_KEY,
  COPILOT_PANEL_LEGACY_KEY,
  COPILOT_CLOSE_EVENT,
  clampPanelSize,
} from './interfaces';
import { shouldShowCopilotExitButton } from './defaultAgent';

// --- Icon/Label helpers ---
function getExpandIcon(size: CopilotPanelSize, willShrink: boolean) {
  // Figma: < at default (expand to medium), << at wide (expand to full), >> at wider (collapse to default)
  if (size === 'wider') return ChevronDoubleRightRegular;
  if (size === 'wide') return ChevronDoubleLeftRegular;
  return ChevronLeftRegular;
}

function getExpandLabel(size: CopilotPanelSize, isDisabled: boolean, willShrink: boolean): string {
  if (isDisabled) return 'Maximum size for this viewport';
  if (willShrink) return 'Collapse to default';
  if (size === 'default') return 'Expand to medium';
  return 'Expand to full';
}

// --- Component ---
export const MCAPSCopilotPanelWrapper: React.FC<MCAPSCopilotPanelWrapperProps> = ({
  isOpen,
  handoffContext,
  userEmail,
  userFullName,
  onClose,
  onExitTileContext,
  context = 'tile',
  isDefaultAgentEnabled = false,
  apiUrl,
  themeMode = 'light',
  initialPrompt,
  onInitialPromptApplied
}) => {
  const reduxDispatch = useDispatch();
  const serviceContext = useContext(ServiceContext);
  const loadHandoff = useCallback(() =>
    loadCopilotAccessMap(serviceContext.authClient, userEmail || ''),
  [serviceContext.authClient, userEmail]);
  const mcapsTelemetry = useMCAPSCopilotTelemetry(reduxDispatch);

  // 3-state panel: desiredSize = user intent, effectiveSize = after viewport clamping
  const [desiredSize, setDesiredSize] = useState<CopilotPanelSize>('default');
  const [effectiveSize, setEffectiveSize] = useState<CopilotPanelSize>('default');

  // --- Mount: always start at default, clear any legacy keys ---
  useEffect(() => {
    try {
      // Always start at default on mount/reload
      sessionStorage.setItem(COPILOT_PANEL_STORAGE_KEY, 'default');
      // Remove legacy key
      sessionStorage.removeItem(COPILOT_PANEL_LEGACY_KEY);
    } catch {}
  }, []);

  // --- Sync effectiveSize from desiredSize + viewport ---
  useEffect(() => {
    setEffectiveSize(clampPanelSize(desiredSize, window.innerWidth));
  }, [desiredSize]);

  // --- Responsive resize listener ---
  useEffect(() => {
    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setEffectiveSize(clampPanelSize(desiredSize, window.innerWidth));
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [desiredSize]);

  // --- Body class + CSS variable sync ---
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('mcaps-panel-open');
      document.body.classList.remove('mcaps-panel-wide', 'mcaps-panel-wider');
      if (effectiveSize === 'wide') document.body.classList.add('mcaps-panel-wide');
      if (effectiveSize === 'wider') document.body.classList.add('mcaps-panel-wider');
      document.documentElement.style.setProperty('--mcaps-copilot-width', COPILOT_PANEL_WIDTHS[effectiveSize]);
    } else {
      document.body.classList.remove('mcaps-panel-open', 'mcaps-panel-wide', 'mcaps-panel-wider');
      document.documentElement.style.removeProperty('--mcaps-copilot-width');
    }
    return () => {
      document.body.classList.remove('mcaps-panel-open', 'mcaps-panel-wide', 'mcaps-panel-wider');
      document.documentElement.style.removeProperty('--mcaps-copilot-width');
    };
  }, [isOpen, effectiveSize]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    mcapsTelemetry.logPanelClosed();
    // Reset to default so panel always reopens at 26.4%
    setDesiredSize('default');
    try { sessionStorage.setItem(COPILOT_PANEL_STORAGE_KEY, 'default'); } catch {}
    const mcapsButton = document.getElementById("mcapshelp-copilot");
    if (mcapsButton) mcapsButton.classList.remove("is-checked");
    if (!isDefaultAgentEnabled) {
      const irisButton = document.getElementById("Iris");
      if (irisButton) {
        irisButton.classList.add("is-checked");
        irisButton.focus();
      }
    }
    onClose();
  }, [isDefaultAgentEnabled, mcapsTelemetry, onClose]);

  // --- Listen for programmatic close requests (from InfoPanel/Tile) ---
  useEffect(() => {
    const handleCloseRequest = () => { if (isOpen) handleClose(); };
    window.addEventListener(COPILOT_CLOSE_EVENT, handleCloseRequest);
    return () => window.removeEventListener(COPILOT_CLOSE_EVENT, handleCloseRequest);
  }, [isOpen, handleClose]);

  // --- Refresh handler ---
  const handleRefreshCopilot = useCallback(() => {
    window.dispatchEvent(new CustomEvent('mcapsCopilot_triggerReset', {
      detail: { timestamp: Date.now(), source: 'refreshButton' }
    }));
    sessionStorage.setItem('mcapsCopilot_triggerFullClear', Date.now().toString());
    mcapsTelemetry.logPanelRefreshed?.();
    setTimeout(() => {
      const chatInput = document.querySelector('.textarea-chat textarea') ||
                        document.querySelector('.textarea-chat [role="textbox"]') ||
                        document.querySelector('.fai-EditorInput textarea') ||
                        document.querySelector('.fai-EditorInput [role="textbox"]');
      if (chatInput && chatInput instanceof HTMLElement) chatInput.focus();
    }, 200);
  }, [mcapsTelemetry]);

  // --- Expand/collapse handler ---
  // Figma behavior: < expands (default→wide→wider), >> collapses directly to default
  const handleExpandCollapse = useCallback(() => {
    setDesiredSize(prev => {
      // If already expanded (wide or wider), collapse to default
      // If default, expand to next size
      const next = prev === 'default' ? 'wide' : (prev === 'wide' ? 'wider' : 'default');
      try { sessionStorage.setItem(COPILOT_PANEL_STORAGE_KEY, next); } catch {}
      mcapsTelemetry.logPanelToggle({ action: 'click', fromSize: prev, toSize: next });
      return next;
    });
  }, [mcapsTelemetry]);

  // --- Dismiss handler ---
  const handlePanelDismiss = useCallback(() => {
    mcapsTelemetry.logPanelClosed();
    // Reset to default so panel always reopens at 26.4%
    setDesiredSize('default');
    try { sessionStorage.setItem(COPILOT_PANEL_STORAGE_KEY, 'default'); } catch {}
    const mcapsButton = document.getElementById("mcapshelp-copilot");
    if (mcapsButton) mcapsButton.classList.remove("is-checked");
    if (!isDefaultAgentEnabled) {
      const irisButton = document.getElementById("Iris");
      if (irisButton) {
        irisButton.classList.add("is-checked");
        irisButton.focus();
      }
    }
    onClose();
  }, [isDefaultAgentEnabled, mcapsTelemetry, onClose]);

  const handleContextExit = useCallback(() => {
    if (isDefaultAgentEnabled && context === 'tile' && onExitTileContext) {
      onExitTileContext();
      return;
    }
    handleClose();
  }, [context, handleClose, isDefaultAgentEnabled, onExitTileContext]);

  // --- Keyboard handler ---
  const handleKeyDown = (event: React.KeyboardEvent<any>, action: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (action === "Refresh") handleRefreshCopilot();
      else if (action === "ExpandCollapse") handleExpandCollapse();
    }
  };

  // --- Compute expand button state ---
  // Figma: default shows <, wide shows <, wider shows >>
  // Clicking < from default → wide, clicking < from wide → wider
  // Clicking >> from wider → default (collapse to default)
  const willShrink = effectiveSize === 'wider';
  const isExpandDisabled = effectiveSize === 'default' && desiredSize === 'default' 
    ? false 
    : (effectiveSize !== 'wider' && clampPanelSize(
        (desiredSize === 'default' ? 'wide' : 'wider') as CopilotPanelSize, window.innerWidth
      ) === effectiveSize);
  const ExpandIcon = getExpandIcon(effectiveSize, willShrink);
  const expandLabel = getExpandLabel(effectiveSize, isExpandDisabled, willShrink);
  const showExitButton = shouldShowCopilotExitButton(
    isDefaultAgentEnabled,
    context
  );

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={isDefaultAgentEnabled ? undefined : handlePanelDismiss}
      type={PanelType.custom}
      customWidth={COPILOT_PANEL_WIDTHS[effectiveSize]}
      closeButtonAriaLabel="Close Copilot Panel"
      isBlocking={false}
      isLightDismiss={!isDefaultAgentEnabled && effectiveSize !== 'wider'}
      layerProps={{ className: 'mcapsCopilotLayer' }}
      className="mcapsCopilotPanel"
      onRenderNavigation={() => null}
      onRenderHeader={() => (
        <div
          className="mcapsCopilotHeader"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            minHeight: "48px"
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'Segoe UI, system-ui, sans-serif'
            }}
          >
            <img
              src="/images/mcapshelp-copilot-icon.svg"
              alt="MCAPS Help Copilot Icon"
              style={{ width: 24, height: 24 }}
            />
            <span
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#323130',
                lineHeight: '22px'
              }}
            >
              MCAPSHelp Agent
            </span>
          </div>
          <div style={{ display: "flex", gap: "2px" }}>
            <IconButton
              disabled={isExpandDisabled}
              title={expandLabel}
              ariaLabel={expandLabel}
              onClick={handleExpandCollapse}
              onKeyDown={(event) => handleKeyDown(event, 'ExpandCollapse')}
              onRenderIcon={() => (
                <ExpandIcon style={{ fontSize: '20px', color: isExpandDisabled ? '#a19f9d' : '#242424' }} />
              )}
            />
            <IconButton
              title="Refresh"
              onClick={handleRefreshCopilot}
              onKeyDown={(event) => handleKeyDown(event, "Refresh")}
              role="button"
              ariaLabel="Refresh"
              onRenderIcon={() => <ArrowClockwiseRegular style={{ fontSize: '20px', color: '#242424' }} />}
            />
            {showExitButton && (
              <IconButton
                ariaLabel={isDefaultAgentEnabled ? "Exit tile context" : "Close Copilot Panel"}
                title={isDefaultAgentEnabled ? "Exit tile context" : "Close"}
                iconProps={{ iconName: "Cancel" }}
                styles={{ root: { color: '#242424' }, icon: { color: '#242424' } }}
                onClick={handleContextExit}
              />
            )}
          </div>
        </div>
      )}
    >
      <div
        style={{
          backgroundColor: '#f3f2f1',
          fontFamily: 'Segoe UI, system-ui, sans-serif',
        }}
      >
        {isOpen && (handoffContext && context === 'tile' ? (
          <TileAgentHandoffContent
            tileName={handoffContext.tileName}
            requestVersion={handoffContext.requestVersion}
            load={loadHandoff}
            userFullName={userFullName}
            onPortalLinkClick={(config, source) =>
              trackTileAgentHandoff(serviceContext.telemetryClient, 'PortalLinkClick', handoffContext.tileId, config, source)}
          >
            {welcomeContent => (
              <SimpleCopilotPanel apiUrl={apiUrl} userEmail={userEmail} userFullName={userFullName}
                themeMode={themeMode} initialPrompt={initialPrompt} onInitialPromptApplied={onInitialPromptApplied}
                welcomeContent={welcomeContent} />
            )}
          </TileAgentHandoffContent>
        ) : <SimpleCopilotPanel
          apiUrl={apiUrl}
          userEmail={userEmail}
          userFullName={userFullName}
          themeMode={themeMode}
          initialPrompt={initialPrompt}
          onInitialPromptApplied={onInitialPromptApplied}
        />)}
      </div>
    </Panel>
  );
};

export default MCAPSCopilotPanelWrapper;
// Generated by Copilot | 2026-10-26 | 30 lines added/updated | Auto | 1321277 isolate handoff content from chat and stop refresh when closed | Time Taken 8 min
// Generated by Copilot | 2026-10-26 | 4 lines added | Auto | 1321278 emit link telemetry using the platform client and selected tile ID | Time Taken 3 min
// Generated by Copilot | 2026-10-26 | 12 lines added/updated/removed | Auto | 1322441 refresh handoff through existing ownership-protected access endpoint | Time Taken 3 min
// Generated by Copilot | 2026-10-27 | 8 lines added/updated/removed | Auto | 1322441 render enabled handoff welcomes inside the existing Copilot panel | Time Taken 2 min
